import { AxiosResponse } from "axios";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";

import * as Sentry from "@sentry/browser";
import { Button, Col, Row, Spin, message } from "antd";
import { FormInstance } from "antd/lib/form/Form";
import AttachmentsSection from "../components/AttachmentsSection";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  ASSIGN_ATTACHMENT_TO_TEMPLATE,
  ASSIGN_CC_CONTACT_LEVEL_TO_TEMPLATE,
  ASSIGN_CONTACT_LEVEL_TO_TEMPLATE,
  GET_CONTACT_LEVELS,
  GET_EMAIL_TEMPLATE,
  GET_TEMPLATE_CATEGORIES,
  UNASSIGN_ATTACHMENT_FROM_TEMPLATE,
  UPDATE_EMAIL_TEMPLATE,
} from "../gql/templates";
import {
  APIError,
  Attachment,
  ContactLevel,
  Template,
  TemplateAttachment,
  TemplateCategory,
  TemplateRecipientContactLevel,
} from "../helpers/shared-interfaces";
import { buildTemplateFormFields } from "../helpers/template";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  request,
  responseHandler,
} from "../helpers/utilities";
import { CREATE_ATTACHMENT } from "../gql/general";

interface State {
  isLoading: boolean;
  emailTemplate: Template;
  id: string;
  body: string;
  attachments: Attachment[];
  attachmentIds: string[];
  templateAttachments: TemplateAttachment[];
  contactLevels: ContactLevel[];
  templateCategories: TemplateCategory[];
  templateContactLevelsIDs: string[];
  templateCcContactLevelsIDs: string[];
}
interface RouteParams {
  id: string;
}

export default class EditEmailTemplate extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  attachmentsReference: RefObject<FormInstance>;

  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      isLoading: true,
      id: "",
      body: "",
      emailTemplate: {
        id: "",
        key: "",
        name: "",
        templateType: "",
        emailSubject: "",
        body: "",
        templateCategory: {
          id: "",
          name: "",
        },
        templateAttachments: [],
        recipientTemplateContactLevels: [],
        recipientTemplateCcContactLevels: [],
      },
      attachments: [],
      attachmentIds: [],
      templateAttachments: [],
      templateContactLevelsIDs: [],
      templateCcContactLevelsIDs: [],
      contactLevels: [],
      templateCategories: [],
    };

    this.reference = React.createRef<FormInstance>();
    this.attachmentsReference = React.createRef<FormInstance>();
  }

  setRef = (
    name: "reference" | "attachmentsReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  getEmailTemplate = async (): Promise<void> => {
    const { id } = this.state;
    this.setState({ isLoading: true });
    const {
      data: { data },
    }: AxiosResponse<{
      data: {
        template: Template;
      };
    }> = await request({ query: GET_EMAIL_TEMPLATE, variables: { id } });
    this.setState({
      body: data.template.body,
      emailTemplate: data.template,
      templateAttachments: data.template.templateAttachments,
      attachments: data.template.templateAttachments.map(
        (templateAttachment: TemplateAttachment) =>
          templateAttachment.attachment,
      ),
      attachmentIds: data.template.templateAttachments.map(
        (templateAttachment: TemplateAttachment) =>
          templateAttachment.attachment.id,
      ),
      templateContactLevelsIDs:
        data.template.recipientTemplateContactLevels.map(
          (contactLevel: TemplateRecipientContactLevel) =>
            contactLevel.contactLevel.id,
        ),
      templateCcContactLevelsIDs:
        data.template.recipientTemplateCcContactLevels.map(
          (contactLevel: TemplateRecipientContactLevel) =>
            contactLevel.contactLevel.id,
        ),
      isLoading: false,
    });
  };

  redirectToEmailPage(): void {
    this.props.history.push(`/communication-templates/${this.state.id}`);
  }

  updateEmailTemplate = async (): Promise<void> => {
    interface Response {
      data?: {
        updateTemplate?: {
          template?: {
            body: string;
          };
          errors?: APIError[];
        };
      };
      errors?: Record<string, unknown>[];
    }

    if (!this.reference.current) {
      return;
    }

    let data;

    try {
      data = await this.reference.current.validateFields([
        "body",
        "name",
        "emailSubject",
        "templateType",
        "templateCategoryId",
        "contactLevelIds",
        "ccContactLevelIds",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      this.setState({
        isLoading: true,
      });

      const response: AxiosResponse<Response> = await request({
        query: UPDATE_EMAIL_TEMPLATE,
        variables: {
          id: this.state.id,
          emailSubject: data.emailSubject,
          templateType: data.templateType,
          templateCategoryId: data.templateCategoryId,
          name: data.name,
          body: data.body,
        },
      });

      if (
        response.data.data?.updateTemplate?.errors &&
        response.data.data?.updateTemplate?.errors?.length > 0
      ) {
        message.error(
          generateDisplayErrorMessage(
            response.data.data?.updateTemplate.errors,
          ),
        );
        this.setState({
          isLoading: false,
        });
        return;
      }

      if (response.data && response.data.data?.updateTemplate) {
        if (data.contactLevelIds) {
          await this.assignContactLevelToTemplate({
            contactLevelIds: data.contactLevelIds,
            templateId: this.state.id,
          });
        }
        if (data.ccContactLevelIds) {
          await this.assignContactLevelToTemplate({
            contactLevelIds: data.ccContactLevelIds,
            templateId: this.state.id,
            isCC: true,
          });
        }
        message.success("Email Template updated sucessfully");
        this.redirectToEmailPage();
      } else {
        message.error("Failed to update Email Template");
      }
    } catch (error) {
      message.error(`Failed to update Template - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
    this.setState({
      isLoading: false,
    });
  };

  assignAttachmentToTemplate = async (
    attachmentIds: string[],
    templateId: string,
  ): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignAttachmentsToTemplate: {
          template: {
            templateAttachments: TemplateAttachment[];
          };
        };
      };
    }

    return request({
      query: ASSIGN_ATTACHMENT_TO_TEMPLATE,
      variables: {
        attachmentIds,
        templateId,
      },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({
        isLoading: false,
        templateAttachments:
          data.data.assignAttachmentsToTemplate.template.templateAttachments,
      });
    });
  };

  unassignAttachmentFromTemplate = async (id: string): Promise<void> => {
    const { templateAttachments } = this.state;

    const templateAttachmentToDelete: TemplateAttachment | undefined =
      templateAttachments.find(
        (cra: TemplateAttachment) => cra.attachment.id === id,
      );

    interface Response {
      result: boolean;
      errors: APIError[];
    }

    try {
      this.setState({ isLoading: true });
      const response: AxiosResponse = await request({
        query: UNASSIGN_ATTACHMENT_FROM_TEMPLATE,
        variables: { id: templateAttachmentToDelete?.id },
      });
      const { mutation } = responseHandler<Response>(
        response,
        "unassignAttachmentFromTemplate",
      );

      if (!mutation.result) {
        message.error("Failed to delete attachement");
      } else {
        const attachments = this.state.attachments.filter(
          (attachment) => attachment.id !== id,
        );
        this.setState({
          attachments,
          isLoading: false,
        });
        message.success("Deleted Attachment Successfully");
      }
    } catch (error) {
      message.error(`An error occurred, please refresh and try again later - ${generateDisplayErrorMessage(error)}`);
    }
  };

  createAttachment = async (combinedInfo: string): Promise<void> => {
    interface Response {
      attachment: Attachment;
    }

    const fieldsToBeValidated: string[] = ["url"];

    this.setState({ isLoading: true });

    try {
      const [url, name] = combinedInfo.split("@@@");

      const response: AxiosResponse = await request({
        query: CREATE_ATTACHMENT,
        variables: {
          url,
          name,
        },
      });
      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "createAttachment",
      );
      this.setState(
        (state) => ({
          attachmentIds: [...state.attachmentIds, mutation.attachment.id],
          attachments: [...state.attachments, mutation.attachment],
        }),
        () =>
          this.assignAttachmentToTemplate(
            this.state.attachmentIds,
            this.state.id,
          ),
      );

      this.attachmentsReference.current &&
        this.attachmentsReference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ isLoading: false });
    }
  };

  getContactLevels = async (): Promise<void> => {
    this.setState({ isLoading: true });
    try {
      const [
        {
          data: {
            data: { contactLevels },
          },
        },
        {
          data: {
            data: { templateCategories },
          },
        },
      ]: [
        AxiosResponse<{
          data: {
            contactLevels: ContactLevel[];
          };
        }>,
        AxiosResponse<{
          data: {
            templateCategories: {
              results: TemplateCategory[];
            };
          };
        }>,
      ] = await Promise.all([
        request({
          query: GET_CONTACT_LEVELS,
        }),
        request({
          query: GET_TEMPLATE_CATEGORIES,
          variables: { numberPerPage: 100 },
        }),
      ]);

      this.setState({
        contactLevels,
        templateCategories: templateCategories.results,
        isLoading: false,
      });
    } catch (error) {
      this.setState({ isLoading: false });
    }
  };

  assignContactLevelToTemplate = async (props: {
    contactLevelIds: string[];
    templateId: string;
    isCC?: boolean;
  }): Promise<void> => {
    interface Response {
      data: {
        assignContactLevelToTemplate: {
          customerInteractionNote: {
            recipientTemplateContactLevel: TemplateRecipientContactLevel;
          };
        };
      };
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: props.isCC
          ? ASSIGN_CC_CONTACT_LEVEL_TO_TEMPLATE
          : ASSIGN_CONTACT_LEVEL_TO_TEMPLATE,
        variables: {
          contactLevelIds: props.contactLevelIds,
          templateId: props.templateId,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "assignContactLevelToTemplate",
      );

      if (err) {
        message.error(`failed to assign contact level to template - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getEmailTemplate();
      this.getContactLevels();
    });
  }

  render(): JSX.Element | null {
    const {
      emailTemplate,
      isLoading,
      body,
      contactLevels,
      templateCategories,
      templateContactLevelsIDs,
      templateCcContactLevelsIDs,
    } = this.state;
    if (!body) return <Spin spinning={true}></Spin>;
    const fields = buildTemplateFormFields({
      contactLevels,
      templateCategories,
      template: emailTemplate,
      body,
      templateContactLevelsIDs,
      templateCcContactLevelsIDs,
    });

    return (
      <Card header="">
        <div className="create-department-container" style={{}}>
          <Spin spinning={isLoading}>
            <Row>
              <Col flex="auto">
                <BaseForm
                  size="large"
                  reference={this.reference}
                  layout="vertical"
                  fields={[...fields]}
                  fieldsToHide={[]}
                />
                <AttachmentsSection
                  reference={this.attachmentsReference}
                  createAttachment={this.createAttachment}
                  attachments={this.state.attachments}
                  showForm={true}
                  handleRemove={this.unassignAttachmentFromTemplate}
                />
                <Button
                  type="primary"
                  onClick={this.updateEmailTemplate}
                  className="base-button"
                  style={{
                    position: "relative",
                    borderRadius: 0,
                    height: "40px",
                    width: "100px",
                  }}
                >
                  Save
                </Button>
              </Col>
            </Row>
          </Spin>
        </div>
      </Card>
    );
  }
}
